// "Several boxes" questions (2026-10-12): a few small typed-number boxes in
// one question. An answer is sent, and its answer key is stored, as one
// string per box, "<box id>=<number>" (for example "a=12", "b=3.5"), and the
// two are marked by comparing those strings. So the number text has to come
// out the same on both sides: canonicalBoxNumber below is the one place that
// decides it.

// Whole digits a number may have. 16 or more is 1e15 or bigger, which the
// server does not accept.
const MAX_WHOLE_DIGITS = 15;

// The one agreed way to write a number: no leading zeros, no trailing zeros
// after the point, no trailing point, no plus sign, never "-0", never
// exponent notation. Built from the typed digits, not from Number(), so very
// small and very large values keep their plain form. Null for anything that
// is not a plain number.
export function canonicalBoxNumber(text: string): string | null {
  // A minus sign may be typed as "-" or as the proper "−" (U+2212).
  const match = /^([-−])?(\d*)(?:\.(\d*))?$/.exec(text.trim());
  if (!match) return null;
  const [, sign, whole = '', fraction = ''] = match;
  if (whole.length === 0 && fraction.length === 0) return null;
  const wholeDigits = whole.replace(/^0+/, '');
  const fractionDigits = fraction.replace(/0+$/, '');
  if (wholeDigits.length > MAX_WHOLE_DIGITS) return null;
  const body =
    (wholeDigits.length > 0 ? wholeDigits : '0') +
    (fractionDigits.length > 0 ? `.${fractionDigits}` : '');
  if (body === '0') return '0';
  return sign === undefined ? body : `-${body}`;
}

// One box's entry, "a=12". Null until the box holds a number.
export function boxEntry(id: string, text: string): string | null {
  const canonical = canonicalBoxNumber(text);
  return canonical === null ? null : `${id}=${canonical}`;
}

// The other way: "a=12" back into its box id and number. Null for anything
// else (a tick-all or put-in-order id, for example).
export function parseBoxEntry(
  entry: unknown,
): { id: string; value: string } | null {
  if (typeof entry !== 'string') return null;
  const match = /^([a-z])=(.+)$/.exec(entry);
  if (!match) return null;
  const [, id = '', value = ''] = match;
  return canonicalBoxNumber(value) === null ? null : { id, value };
}

// Boxes can sit inside the question's sentence, marked "[a]", "[b]" ...
// ("4/5 − 1/15 = [a]/15"). The prompt cut into its plain text and its
// markers, in reading order.
export type BoxPromptPart = string | { boxId: string };

export function splitBoxMarkers(prompt: string): BoxPromptPart[] {
  // split() with a capture group: text, id, text, id ... text.
  return prompt
    .split(/\[([a-z])\]/)
    .map((piece, index): BoxPromptPart =>
      index % 2 === 1 ? { boxId: piece } : piece,
    )
    .filter((part) => part !== '');
}

// A box can be the top or the bottom of a fraction: "[a]/15", "3/[a]",
// and after a whole number, "2 [a]/5". Those are drawn stacked, like the
// ordinary fractions beside them (see MathText.tsx, whose rule this copies:
// 1 to 3 digits, an optional 1 or 2 digit whole number and a space before,
// and nothing joined on -- no more digits, slashes, decimals or markers).
export type BoxFraction = {
  whole: string | undefined;
  numerator: string | { boxId: string };
  denominator: string | { boxId: string };
};

const BOX_FRACTION =
  /(?<![\d/\]]|\d[.,])(?:(\d{1,2}) )?(?:\[([a-z])\]\/(\d{1,3})|(\d{1,3})\/\[([a-z])\])(?![\d/[]|[.,]\d)/g;

// The prompt cut into its box fractions and the text between them, in
// reading order. The text between still holds any other markers (cut it
// with splitBoxMarkers). `isBox` says which letters really are boxes of
// this question; a fraction round any other letter stays as text.
export function splitBoxFractions(
  prompt: string,
  isBox: (boxId: string) => boolean = () => true,
): (string | BoxFraction)[] {
  const parts: (string | BoxFraction)[] = [];
  let last = 0;
  for (const match of prompt.matchAll(BOX_FRACTION)) {
    const [full, whole, topBox, bottomDigits, topDigits, bottomBox] = match;
    if (!isBox(topBox ?? bottomBox ?? '')) continue;
    if (match.index > last) parts.push(prompt.slice(last, match.index));
    parts.push(
      topBox !== undefined
        ? {
            whole,
            numerator: { boxId: topBox },
            denominator: bottomDigits ?? '',
          }
        : {
            whole,
            numerator: topDigits ?? '',
            denominator: { boxId: bottomBox ?? '' },
          },
    );
    last = match.index + full.length;
  }
  if (last < prompt.length) parts.push(prompt.slice(last));
  return parts;
}

// A box inside the sentence has no label of its own, only its marker
// ("[a]"). The letter of such a label, or null for a real label.
export function boxMarkerLetter(label: string): string | null {
  return /^\[([a-z])\]$/.exec(label.trim())?.[1] ?? null;
}
